import BenchlingConfig from 'BenchlingConfig';
import StringResolutionManager from 'models/managers/StringResolutionManager';
import {
  LastSyncedUpstreamValue,
  PrimarySample,
  SchemaFieldColumnUpdate,
  StructuredTable,
  StructuredTableAdapter,
} from 'notebook/structured-tables/StructuredTableConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {removeQueryResolutionsFromSnapshot} from 'notebook/tables/excess/Resolver';
import * as RefreshTableDataDAO from 'notebook/tables/refresh-table-data/RefreshTableDataDAO';
import {updateStructuredTableUsingRowUpdater} from 'notebook/tables/refresh-table-data/updateStructuredTableUsingRowUpdater';
import {Cells} from 'notebook/tables/TableConstants';

export async function fetchColumnUpdatesForTable({
  entryOrTemplateId,
  provenanceUuid,
}: {
  entryOrTemplateId: string;
  provenanceUuid: string;
}): Promise<{[columnId: number]: SchemaFieldColumnUpdate | {}}> {
  const {columnUpdates} = await RefreshTableDataDAO.getColumnUpdates(entryOrTemplateId, provenanceUuid);
  return columnUpdates;
}

export async function handleRefreshStructuredTable({
  entryOrTemplateId,
  computedTable,
  structuredTable,
  structuredTableAdapter,
  onUpdateTable,
}: {
  entryOrTemplateId: string;
  computedTable: Array<Array<EvaluatedCell>>;
  structuredTable: StructuredTable<unknown, unknown>;
  structuredTableAdapter: StructuredTableAdapter<unknown, unknown>;
  onUpdateTable: ({
    updatedCells,
    columnUpdates,
    lastSyncedUpstreamValuesByRow,
    numApiRowsToAdd,
  }: {
    updatedCells: Cells;
    columnUpdates: {[columnId: number]: SchemaFieldColumnUpdate | {}};
    lastSyncedUpstreamValuesByRow: Array<Array<LastSyncedUpstreamValue>>;
    numApiRowsToAdd?: number;
  }) => void;
}): Promise<void> {
  // Clear potentially stale string resolution query cache if rendering from note runtime snapshots
  if (BenchlingConfig.ELN_ENABLE_RUNTIME_SNAPSHOT_RENDER) {
    StringResolutionManager.clearStringResolutionReduxCache();
  }

  // Delete potentially stale queryResolutions from snapshot if snapshot eval is enabled
  if (BenchlingConfig.ELN_ENABLE_RUNTIME_SNAPSHOT_EVAL) {
    removeQueryResolutionsFromSnapshot(entryOrTemplateId, structuredTable.provenanceUuid, {
      [structuredTable.name]: {type: 'eval-table'},
    });
  }

  let samplesByRowId: {[rowId: string]: Array<PrimarySample>};
  if (computedTable.length > 0) {
    samplesByRowId = structuredTableAdapter.getPrimarySamples(structuredTable, computedTable);
  } else {
    samplesByRowId = structuredTableAdapter.getPrimarySamples(structuredTable);
  }
  const updatedTable = await updateStructuredTableUsingRowUpdater({
    rowUpdaters: structuredTableAdapter.getAllRowUpdaters(structuredTable.columns),
    samplesByRowId,
    structuredTable,
  });

  const lastSyncedValuesFromRowUpdater = updatedTable.rows.reduce(
    (valuesByRow, row) => [...valuesByRow, row.lastSyncedUpstreamValues ?? []],
    [] as Array<Array<LastSyncedUpstreamValue>>
  );

  // Wrap the callback function to merge in the lastSyncedUpstreamValues from our row updaters and the
  // lastSyncedUpstreamValues we receive from the core Refresh logic.  The core Refresh logic generates
  // lastSyncedUpstreamValues for basics like our bioentities and storables and their tag values.
  // The StructuredTableRowUpdater is a way for us to extend beyond that core logic and support tracking
  // changes in other columns and typedValue types.  We should always prefer using the values provided by
  // the StructuredTableRowUpdater over the core Refresh logic if they overlap as there may be custom
  // behavior that a particular table wants to support.
  const wrappedOnUpdateTable = ({
    updatedCells,
    columnUpdates,
    numApiRowsToAdd,
  }: {
    updatedCells: Cells;
    columnUpdates: {[columnId: number]: SchemaFieldColumnUpdate | {}};
    numApiRowsToAdd?: number;
  }): void => {
    onUpdateTable({
      updatedCells,
      columnUpdates,
      lastSyncedUpstreamValuesByRow: lastSyncedValuesFromRowUpdater,
      numApiRowsToAdd,
    });
  };

  const table = structuredTableAdapter.toTable(updatedTable);

  const columnUpdates = await fetchColumnUpdatesForTable({
    entryOrTemplateId,
    provenanceUuid: structuredTable.provenanceUuid,
  });
  const numApiRowsToAdd = structuredTable.numApiRowsToAdd;
  wrappedOnUpdateTable({updatedCells: table.table, columnUpdates, numApiRowsToAdd});
}
